import { Link } from "react-router-dom";
import {
  ShieldCheck,
  Package,
  Sparkles,
  Boxes,
  Tag,
  Truck,
  Clock,
  Layers,
  Recycle,
  Award,
  Wallet,
  Compass,
  ArrowRight,
} from "lucide-react";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";

const Sections = ({ city }) => (
  <>
    {/* ======= SECTION 2 — Livraison à domicile ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-7">
            <div className="overline" style={{ color: "#237cb5" }}>
              Livraison à domicile
            </div>
            <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Votre livraison de fournitures directement chez vous
              {" "}{city.aCity}
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Parce que votre temps est précieux et que charger des
              packs de cartons plats volumineux dans votre véhicule
              personnel est une corvée fatigante, notre service de
              proximité s&apos;occupe de tout.
            </p>
            <p className="mt-4 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Nous livrons l&apos;intégralité de vos commandes à plat
              directement à votre domicile {city.aCity}, ainsi que
              dans les localités voisines comme Conflans-Sainte-Honorine,
              Andrésy, Neuville-sur-Oise, Triel-sur-Seine et
              Jouy-le-Moutier. Vos fournitures pros vous sont remises
              sur un créneau horaire précis fixé selon vos
              disponibilités, dans un état de propreté irréprochable
              et totalement sèches pour vous permettre d&apos;attaquer
              vos pièces immédiatement.
            </p>
          </div>

          <div className="lg:col-span-5 space-y-4">
            {[
              {
                title: "Couverture locale réactive",
                body: "Maurecourt, Conflans-Sainte-Honorine, Andrésy, Neuville-sur-Oise, Triel-sur-Seine, Jouy-le-Moutier.",
                Icon: Truck,
              },
              {
                title: "Créneau précis respecté",
                body: "Rendez-vous fixé selon vos disponibilités, pas de demi-journée bloquée à attendre.",
                Icon: Clock,
              },
              {
                title: "État irréprochable",
                body: "Cartons à plat, propres et totalement secs pour attaquer vos pièces sans délai.",
                Icon: ShieldCheck,
              },
            ].map(({ title, body, Icon }) => (
              <div
                key={title}
                className="flex items-start gap-4 bg-white border border-[#cfdbe6] rounded-2xl p-5 md:p-6"
              >
                <span className="flex items-center justify-center shrink-0 h-11 w-11 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <div>
                  <h3 className="font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                    {title}
                  </h3>
                  <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                    {body}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    {/* ======= SECTION 3 — Alternative verte : reconditionnés ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-7">
            <div className="overline" style={{ color: "#237cb5" }}>
              Alternative verte
            </div>
            <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              L&apos;alternative verte : nos cartons reconditionnés de
              qualité pro
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Faites un geste pour la planète et pour votre budget !
              Pour votre déménagement {city.aCity}, nous mettons à
              votre disposition notre gamme exclusive de cartons
              professionnels reconditionnés ayant servi une seule et
              unique fois. Méticuleusement inspectés, triés et stockés
              au sec par nos équipes logistiques, ces cartons
              conservent 100 % de leur rigidité structurelle et de leur
              pouvoir de protection.
            </p>
            <p className="mt-4 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              En optant pour cette solution éco-responsable, vous
              bénéficiez d&apos;une réduction immédiate sur le prix du
              neuf, tout en participant activement à l&apos;économie
              circulaire en réduisant les déchets d&apos;emballage
              inutiles.
            </p>

            <div className="mt-7 flex flex-wrap gap-3">
              <Link
                to="/cartons-demenagement"
                className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
                data-testid="city-maurecourt-cta-reconditionnes"
              >
                <Recycle className="h-4 w-4" strokeWidth={2.6} />
                Voir nos cartons reconditionnés
                <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
              </Link>
              <Link
                to="/kits-cartons"
                className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
                data-testid="city-maurecourt-cta-kits"
              >
                <Boxes className="h-4 w-4" strokeWidth={2.6} />
                Découvrir nos kits
              </Link>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-6 md:p-7 space-y-4">
              <div className="flex items-center gap-3">
                <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#237cb5] text-white">
                  <Recycle className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h3 className="font-display font-extrabold text-[18px] text-[#0f1d2b] leading-tight">
                  Économie circulaire
                </h3>
              </div>
              <div className="space-y-2.5 text-[14px] text-[#3a4856]">
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                  Utilisés 1 seule fois
                </div>
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                  Triés et inspectés à la main
                </div>
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                  Stockés au sec
                </div>
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                  100 % de la rigidité d&apos;origine
                </div>
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                  Réduction immédiate sur le prix neuf
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* ======= CTA Big Déménagement (au milieu) ======= */}
    <section className="py-12 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <BigDemenageurCTA testId={`city-${city.slug}-bigdem-cta-middle`} />
      </div>
    </section>

    {/* ======= SECTION 4 — Pourquoi choisir notre boutique ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Nos engagements
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Pourquoi choisir notre boutique pour votre déménagement ?
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Faire confiance à notre service de fournitures de
            déménagement, c&apos;est s&apos;assurer d&apos;une
            transition réussie grâce à des engagements clairs.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
          {[
            {
              title: "Robustesse industrielle",
              body: "Tous nos cartons et accessoires (film à bulles, adhésifs, housses) sont identiques à ceux utilisés par les déménageurs professionnels, garantissant une résistance maximale à la déchirure et à l'écrasement.",
              Icon: Layers,
            },
            {
              title: "Gain de temps absolu",
              body: "Plus besoin de courir les magasins de bricolage. Vous commandez tout votre matériel en quelques clics depuis votre canapé et vous êtes livré directement à votre porte.",
              Icon: Clock,
            },
            {
              title: "Prix transparents et compétitifs",
              body: "Du carton à l'unité aux kits complets tout-en-un pour votre logement, nous proposons des tarifs justes et adaptés à tous les volumes de déménagement.",
              Icon: Wallet,
            },
            {
              title: "Expertise et conseil local",
              body: "En tant que spécialistes de la logistique en Île-de-France, nous connaissons vos besoins et vous guidons sur le choix et la quantité de matériel pour éviter toute panne de carton le jour J.",
              Icon: Compass,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="flex items-start gap-4 bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-5 md:p-6"
            >
              <span className="flex items-center justify-center shrink-0 h-11 w-11 rounded-full bg-[#237cb5] text-white">
                <Icon className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <div>
                <h3 className="font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                  {title}
                </h3>
                <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                  {body}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/cartons-demenagement"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-maurecourt-cta-shop"
          >
            <Boxes className="h-4 w-4" strokeWidth={2.6} />
            Voir toute la gamme
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
          <Link
            to="/fermeture-accessoires"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="city-maurecourt-cta-accessoires"
          >
            <Tag className="h-4 w-4" strokeWidth={2.6} />
            Adhésifs &amp; protections
          </Link>
        </div>
      </div>
    </section>

    {/* ======= SECTION 5 — CTA retour département ======= */}
    <section className="py-10 md:py-14 bg-[#EEF3F7] border-t border-[#cfdbe6]">
      <div className="container-cdm">
        <Link
          to={`/cartons-demenagement/${city.deptSlug}`}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
          data-testid="city-maurecourt-cta-back-dept"
        >
          <Package className="h-4 w-4" strokeWidth={2.6} />
          Voir la page {city.deptName} ({city.deptCode})
        </Link>
      </div>
    </section>
  </>
);

export default { Sections };
